import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from "@devhop/ui";
import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import type { VoidReceiptPreviewDTO } from "../api/client";
import {
  useVoidReceiptMutation,
  useVoidReceiptPreviewQuery,
} from "../api/queries";

export type VoidReceiptDialogProps = {
  receiptId: string;
  receiptNo: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

function ImpactSummary({ preview }: { preview: VoidReceiptPreviewDTO }) {
  if (!preview.canVoid) {
    return (
      <p className="text-destructive text-sm">
        {preview.blockReason ?? "ບໍ່ສາມາດຍົກເລີກໃບເກັບເງິນນີ້"}
      </p>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-4 text-sm">
      <p className="font-medium">ຜົນກະທົບທີ່ຈະຖືກລຶບ:</p>
      <ul className="list-inside list-disc space-y-1 text-muted-foreground">
        {preview.classPackages > 0 && (
          <li>ແພັກເກດຄລາດສ໌: {preview.classPackages} ລາຍການ</li>
        )}
        {preview.privateLessonPackages > 0 && (
          <li>
            ແພັກເກດຮຽນສ່ວນຕົວ: {preview.privateLessonPackages} ລາຍການ
          </li>
        )}
        {preview.roomBookings > 0 && (
          <li>ການຈອງຫ້ອງ: {preview.roomBookings} ລາຍການ</li>
        )}
        {preview.estimatedBookings > 0 && (
          <li>ການຈອງ PL: {preview.estimatedBookings} ລາຍການ</li>
        )}
        {preview.estimatedAttendances > 0 && (
          <li>ບັນທຶກເຊັກຊື່: {preview.estimatedAttendances} ລາຍການ</li>
        )}
        {preview.classPackages === 0 &&
          preview.privateLessonPackages === 0 &&
          preview.roomBookings === 0 &&
          preview.estimatedBookings === 0 &&
          preview.estimatedAttendances === 0 && (
            <li>ບໍ່ມີຂໍ້ມູນດຳເນີນການທີ່ຜູກກັບແພັກ</li>
          )}
      </ul>
      <p className="pt-2 text-muted-foreground text-xs">
        ໃບເກັບເງິນຈະຖືກຍົກເລີກ — ຂໍ້ມູນນັກຮຽນຈະບໍ່ຖືກລຶບ
      </p>
    </div>
  );
}

export function VoidReceiptDialog({
  receiptId,
  receiptNo,
  open,
  onOpenChange,
  onSuccess,
}: VoidReceiptDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: preview, isLoading: isPreviewLoading } =
    useVoidReceiptPreviewQuery(receiptId, open);
  const voidMutation = useVoidReceiptMutation(receiptId);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("ກະລຸນາປ້ອນເຫດຜົນ");
      return;
    }
    if (!preview?.canVoid) return;

    setError(null);
    try {
      await voidMutation.mutateAsync({ reason: trimmed });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      const msg =
        e instanceof Error && e.message ? e.message : "ຍົກເລີກໃບເກັບເງິນລົ້ມເຫຼວ";
      setError(msg);
    }
  };

  const isSubmitting = voidMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        onPointerDownOutside={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>ຍົກເລີກໃບເກັບເງິນ</DialogTitle>
          <p className="text-muted-foreground text-sm">
            ໃບເກັບເງິນ {receiptNo} — ການດຳເນີນການນີ້ບໍ່ສາມາດຍ້ອນກັບໄດ້
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isPreviewLoading ? (
            <div className="flex justify-center py-6">
              <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : preview ? (
            <ImpactSummary preview={preview} />
          ) : (
            <p className="text-destructive text-sm">ບໍ່ສາມາດໂຫຼດຂໍ້ມູນ preview</p>
          )}

          {preview?.canVoid && (
            <div className="space-y-2">
              <Label htmlFor="void-reason">
                ເຫດຜົນ <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="void-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="ປ້ອນເຫດຜົນການຍົກເລີກ..."
                rows={4}
                className="resize-none"
                disabled={isSubmitting}
              />
            </div>
          )}

          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            ປິດ
          </Button>
          {preview?.canVoid && (
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={isSubmitting || isPreviewLoading}
            >
              {isSubmitting ? "ກຳລັງຍົກເລີກ..." : "ຢືນຢັນຍົກເລີກ"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
